import { z } from 'zod';

import { CashMovementDirectionSchema, CashMovementKindSchema } from './cash-movement.schema.js';
import {
  cashReportTimestampSchema,
  CashReportActorRefDtoSchema,
  CashReportCashLocationRefDtoSchema,
  CashReportCategoryBreakdownDtoSchema,
  CashReportMovementCategorySchema,
  CashReportPhysicalEffectSchema,
  CashReportPurchasePayoutRefDtoSchema,
  CashReportSessionRefDtoSchema,
  CashReportSourceRefDtoSchema,
  CashReportVarianceActionRefDtoSchema,
  CashReportVarianceCaseRefDtoSchema,
  validateCashReportDateRange,
} from './cash-report-common.schema.js';
import type {
  CashTreasuryVaultBalanceCardDto,
  CashTreasuryVaultRecentActivityRowDto,
  GetCashTreasuryVaultVisibilityReportRequest,
  GetCashTreasuryVaultVisibilityReportResponse,
} from './cash-report-treasury.dto.js';

export const GetCashTreasuryVaultVisibilityReportRequestSchema: z.ZodType<GetCashTreasuryVaultVisibilityReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      reportCategory: CashReportMovementCategorySchema.optional(),
      from: cashReportTimestampSchema.optional(),
      to: cashReportTimestampSchema.optional(),
      asOf: cashReportTimestampSchema.optional(),
    })
    .superRefine(validateCashReportDateRange);

export const CashTreasuryVaultBalanceCardDtoSchema: z.ZodType<CashTreasuryVaultBalanceCardDto> =
  z.object({
    cashLocation: CashReportCashLocationRefDtoSchema,
    balanceMinor: z.number().int(),
    lastMovementAt: cashReportTimestampSchema.nullable(),
    asOf: cashReportTimestampSchema,
    categoryBreakdown: z.array(CashReportCategoryBreakdownDtoSchema).optional(),
  });

export const CashTreasuryVaultRecentActivityRowDtoSchema: z.ZodType<CashTreasuryVaultRecentActivityRowDto> =
  z.object({
    movementId: z.uuid(),
    occurredAt: cashReportTimestampSchema,
    reportCategory: CashReportMovementCategorySchema,
    movementKind: CashMovementKindSchema,
    direction: CashMovementDirectionSchema,
    physicalEffect: CashReportPhysicalEffectSchema,
    amountMinor: z.number().int().nonnegative(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema.nullable(),
    varianceCase: CashReportVarianceCaseRefDtoSchema.nullable(),
    varianceAction: CashReportVarianceActionRefDtoSchema.nullable(),
    purchasePayout: CashReportPurchasePayoutRefDtoSchema.nullable(),
    source: CashReportSourceRefDtoSchema.nullable(),
    fundingSourceCategoryId: z.uuid().nullable().optional(),
    fundingSourceCategoryCodeSnapshot: z.string().trim().min(1).max(80).nullable().optional(),
    fundingSourceCategoryDisplayNameSnapshot: z.string().trim().min(1).max(120).nullable().optional(),
    actor: CashReportActorRefDtoSchema.nullable(),
  });

export const GetCashTreasuryVaultVisibilityReportResponseSchema: z.ZodType<GetCashTreasuryVaultVisibilityReportResponse> =
  z.object({
    asOf: cashReportTimestampSchema,
    balanceCards: z.array(CashTreasuryVaultBalanceCardDtoSchema),
    recentActivity: z.array(CashTreasuryVaultRecentActivityRowDtoSchema),
    supportedMovementCategories: z.array(CashReportMovementCategorySchema),
  });
